import { ApiCompany } from "../../core/apis/company";
import { AppRouteUi } from "../../config/appRoute";
import { Button } from "../../components/button.component";
import { Form, Toggle } from "rsuite";
import { useHistory } from "react-router";
import { useCallback, useEffect, useState } from "react";
import {
  TextInput,
  PasswordInput,
  TagInputWithIcon,
} from "../../components/textInput.component";
import {
  DoesObjectHasAnyInvalidValue,
  generateMultipartFormData,
} from "../../utils/formUtils";
import { ActionsCompanyTable } from "../../core/redux/slices/sliceCompanyTable";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { CompanyRegisterFormSchema } from "../../models/forms/companyRegisterForm";
import { ApiInsuranceTypes } from "../../core/apis/insuranceTypes";

type CompanyFormData = {
  CompanyName?: string;
  Email?: string;
  AdminFirstName?: string;
  AdminLastName?: string;
  AdminEmail?: string;
  AdminPhoneNumber?: string;
  AdminPassword?: string;
  AdminConfirmPassword?: string;
  AdminUserName?: string;
  ProviderNumber?: string;
  insuranceTypeIds: string[];
};

export const AddCompany = () => {
  const routeHistory = useHistory();
  const [isAutoClockInEnable, setIsAutoClockInEnable] = useState(false);
  const [isGpsCheckInEnable, setIsGpsCheckInEnable] = useState(false);
  const { data: insuranceTypes } = ApiInsuranceTypes.GetInsuranceTypes.Hook();
  const [formError, setFormError] = useState({});

  const insuranceTypeMap =
    insuranceTypes?.map((it) => ({
      label: it.displayName ?? it.name,
      value: it.sKey,
    })) ?? [];

  const [formData, setFormData] = useState<CompanyFormData>({
    CompanyName: "",
    Email: "",
    AdminFirstName: "",
    AdminLastName: "",
    AdminEmail: "",
    AdminPhoneNumber: "",
    AdminPassword: "",
    AdminConfirmPassword: "",
    AdminUserName: "",
    ProviderNumber: "",
    insuranceTypeIds: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useAppDispatch();
  useEffect(() => {
    const isEmpty = Object.values({ ...formData }).some(
      DoesObjectHasAnyInvalidValue
    );
    const errorCount = Object.keys(formError).length;
    const isPasswordMatched =
      formData.AdminPassword !== "" &&
      formData.AdminConfirmPassword !== "" &&
      formData.AdminPassword?.match(formData.AdminConfirmPassword!);
    const isDisabled = isEmpty || !!errorCount || !isPasswordMatched || !formData?.insuranceTypeIds?.length;
    setIsDisabled(isDisabled);
  }, [formData, formError]);

  const handleChange = useCallback(
    (data: any, _: any) =>
      setFormData((prevFormData) => ({ ...prevFormData, ...data })),
    []
  );

  const convertToPhoneNumberFormat = (phoneNumber: string, propertyName: string) => {
    phoneNumber = phoneNumber.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
  
    if (phoneNumberLength > 3 && phoneNumberLength < 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumberLength >= 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  
    setFormData((prev) => ({ ...prev, [propertyName]: phoneNumber }));
  }

  const handleSubmit = async () => {
    setIsLoading(true);
    const multipartFormData = generateMultipartFormData(formData);

    multipartFormData.append(
      "IsAutoClockInEnable",
      isAutoClockInEnable ? "true" : "false"
    );

    multipartFormData.append(
      "IsGpsCheckInEnable",
      isGpsCheckInEnable ? "true" : "false"
    );

    const result = await ApiCompany.Add(multipartFormData);
    if (result.isSuccess) {
      dispatch(ActionsCompanyTable.goToPage(1));
      routeHistory.length > 1
        ? routeHistory.goBack()
        : routeHistory.push(AppRouteUi.Root);
    }
    setIsLoading(false);
  };

  // const disabledInsuranceType = () => {
  //   const disabledIT: string[] = [];
  //   insuranceTypes?.forEach((it, idx) => {
  //     if (it.isDisabled) disabledIT.push(insuranceTypeMap[idx].value!);
  //   });
  //   return disabledIT;
  // };

  return (
    <Form
      className="row"
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCheck={setFormError}
      model={CompanyRegisterFormSchema}
      formValue={formData}
      fluid
    >
      <div className="col-md-10 col-12 border-end px-4 pb-3 add-page-ctl">
        <div className="row">
          <TextInput name="CompanyName" required>
            Company Name
          </TextInput>
          <TextInput name="Email" required>
            Company Email
          </TextInput>
        </div>
        <div className="row">
          <TextInput name="ProviderNumber" required>
            NPI/Provider Number
          </TextInput>
          {/* <SelectPickerInput
            name="InsuranceTypeId"
            data={insuranceTypeMap}
            renderEmpty={<p className="text-muted p-3">No Data Available</p>}
            disabledItemValues={disabledInsuranceType()}
          >
            Insurance type
          </SelectPickerInput> */}

          <TagInputWithIcon
            name="insuranceTypeIds"
            data={insuranceTypeMap}
            required
            value={formData.insuranceTypeIds}
            placeholder="Select Insurance Types"
            renderEmpty={<p className="text-muted p-3">No Data Available</p>}
          >
            Insurance type
          </TagInputWithIcon>
        </div>

        <div className="row">
          <TextInput name="AdminFirstName" required>
            Admin First Name
          </TextInput>
          <TextInput name="AdminLastName" required>
            Admin Last Name
          </TextInput>
        </div>

        <div className="row">
          <TextInput name="AdminUserName" fullWidth required>
            Admin Username
          </TextInput>
        </div>

        <div className="row">
          <TextInput name="AdminEmail" required>
            Admin Email
          </TextInput>
          <TextInput 
          name="AdminPhoneNumber" 
          required
          value={formData.AdminPhoneNumber}
          onChange={(val) => convertToPhoneNumberFormat(val, "AdminPhoneNumber")}
          >
            Admin Phone Number
          </TextInput>
        </div>

        <div className="row">
          <PasswordInput name="AdminPassword" required>
            Password
          </PasswordInput>
          <div className="col-md-6 px-1">
            <PasswordInput
              name="AdminConfirmPassword"
              itemType="password"
              required
              fullWidth
            >
              Confirm Password
            </PasswordInput>
            {formData.AdminPassword &&
              formData.AdminConfirmPassword &&
              formData.AdminPassword !== formData.AdminConfirmPassword && (
                <p className="text-red-300">Password did not match</p>
              )}
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 align-items-center px-1 mb-3">
            Auto clock check in
            <Toggle
              onChange={() => setIsAutoClockInEnable(!isAutoClockInEnable)}
              checked={isAutoClockInEnable}
              size="sm"
              className="ms-2"
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-3 align-items-center px-1 mb-3">
            GPS check in
            <Toggle
              onChange={() => setIsGpsCheckInEnable(!isGpsCheckInEnable)}
              checked={isGpsCheckInEnable}
              size="sm"
              className="ms-2"
            />
          </div>
        </div>
      </div>

      <div className="col-md-2 col-12">
        <Button
          className="m-1 w-100 btn-green"
          type="submit"
          name="Save"
          loading={isLoading}
          disabled={isDisabled}
        />
        <Button
          onClick={() => {
            routeHistory.length > 1
              ? routeHistory.goBack()
              : routeHistory.push(AppRouteUi.Root);
          }}
          type="button"
          className="m-1 w-100 btn-green-subtle"
          disabled={isLoading}
          name="Return without saving"
        />
      </div>
    </Form>
  );
};
